import { useState } from "react";

import * as Sentry from "@sentry/nextjs";
import { useTranslations } from "next-intl";

import { updateDocumentLock } from "@/api";
import { FormToggle } from "@/components/form/FormToggle";
import { Icon } from "@/components/icons/Icon";
import { MENU_ITEM_BUTTON_CLASS, MenuItemContent } from "@/components/sidebar/MenuItemContent";
import { twMerge } from "@/utils/twMerge";

import type { DropdownItemRenderProps } from "@/components/sidebar/Dropdown";

export function LockEditingButton({
  uid,
  locked: initialLocked,
  onLockedChange,
  ...props
}: DropdownItemRenderProps & {
  uid: string;
  locked: boolean;
  onLockedChange?: (locked: boolean) => void;
}) {
  const t = useTranslations();
  const [locked, setLocked] = useState(initialLocked);

  const handleClick = async (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    const next = !locked;
    setLocked(next);
    onLockedChange?.(next);
    try {
      await updateDocumentLock(uid, { locked: next });
    } catch (e) {
      setLocked(!next);
      onLockedChange?.(!next);
      Sentry.captureException(e);
    }
  };

  return (
    <button
      type="button"
      {...props}
      onClick={handleClick}
      aria-label={t("documentTree.lockEditing")}
      className={twMerge(MENU_ITEM_BUTTON_CLASS, "outline-none")}
    >
      <MenuItemContent
        item={{
          title: t("documentTree.lockEditing"),
          icon: <Icon name={locked ? "LockLocked" : "LockUnlocked"} />,
        }}
      />
      <FormToggle checked={locked} className="ml-auto" />
    </button>
  );
}
